import React, { useEffect } from 'react';
import { ErrorBoundary as ReactErrorBoundary } from 'react-error-boundary';

// Fallback component that renders nothing but logs the error
function ErrorFallback({ error, resetErrorBoundary, errorInfo }) {
  useEffect(() => {
    // Hide React's development error overlay
    if (process.env.NODE_ENV === 'development') {
      // Remove the error overlay from the DOM
      const errorOverlay = document.querySelector('div[data-reactroot] ~ div, iframe[src*="/__react_error_overlay"]');
      if (errorOverlay) {
        errorOverlay.remove();
      }
      
      // Also try to remove the error overlay container
      const overlayContainer = document.querySelector('div[id="error-overlay"]');
      if (overlayContainer) {
        overlayContainer.remove();
      }

      // Hide any other error overlays
      const allOverlays = document.querySelectorAll('div[style*="position: fixed"][style*="z-index"]');
      allOverlays.forEach(overlay => {
        if (overlay.innerHTML.includes('ERROR') || overlay.innerHTML.includes('Uncaught')) {
          overlay.style.display = 'none';
        }
      });
    }
  }, []);

  // Log the error quietly to console
  console.error('ErrorBoundary caught an error:', {
    error: error.message,
    stack: error.stack,
    errorInfo,
    timestamp: new Date().toISOString(),
    isDevelopment: process.env.NODE_ENV === 'development'
  });

  // Return null to render nothing on screen
  return null;
}

// Error handler function for logging
function logError(error, errorInfo) {
  // Prevent the default error behavior in development
  if (process.env.NODE_ENV === 'development') {
    // Stop the error from propagating to React's error overlay
    error.preventDefault?.();
  }

  console.error('ErrorBoundary logged error:', {
    error: error.message,
    stack: error.stack,
    errorInfo,
    timestamp: new Date().toISOString(),
    environment: process.env.NODE_ENV
  });

  // Hide React error overlay if it appears
  setTimeout(() => {
    if (process.env.NODE_ENV === 'development') {
      const errorOverlays = document.querySelectorAll('iframe[src*="error"], div[data-reactroot] ~ div');
      errorOverlays.forEach(overlay => {
        if (overlay.style) {
          overlay.style.display = 'none';
        }
      });
    }
  }, 100);
}

// Custom ErrorBoundary wrapper component
export function ErrorBoundary({ children, onError = logError }) {
  useEffect(() => {
    // Add CSS to hide error overlays
    const style = document.createElement('style');
    style.textContent = `
      /* Hide React development error overlay */
      iframe[src*="error"], 
      iframe[src*="/__react_error_overlay"],
      div[data-reactroot] ~ div[style*="position: fixed"],
      div[id*="error-overlay"],
      div[class*="error-overlay"] {
        display: none !important;
        visibility: hidden !important;
        opacity: 0 !important;
      }
    `;
    document.head.appendChild(style);

    // Override window error handler to prevent showing error overlays
    const originalErrorHandler = window.onerror;
    const originalUnhandledRejection = window.onunhandledrejection;

    window.onerror = (message, source, lineno, colno, error) => {
      // Log the error but don't show overlay
      console.error('Window error caught by ErrorBoundary:', {
        message,
        source,
        lineno,
        colno,
        error: error?.message,
        stack: error?.stack,
        timestamp: new Date().toISOString()
      });
      
      // Hide any error overlays that might appear
      setTimeout(() => {
        const overlays = document.querySelectorAll('div[style*="position: fixed"], iframe[src*="error"]');
        overlays.forEach(overlay => {
          if (overlay.textContent?.includes('ERROR') || overlay.textContent?.includes('Uncaught')) {
            overlay.style.display = 'none';
          }
        });
      }, 50);
      
      return true; // Prevent default error handling
    };

    window.onunhandledrejection = (event) => {
      console.error('Unhandled promise rejection caught by ErrorBoundary:', {
        reason: event.reason,
        timestamp: new Date().toISOString()
      });
      event.preventDefault(); // Prevent default handling
    };

    return () => {
      window.onerror = originalErrorHandler;
      window.onunhandledrejection = originalUnhandledRejection;
    };
  }, []);

  return (
    <ReactErrorBoundary
      FallbackComponent={ErrorFallback}
      onError={onError}
      onReset={() => {
        console.log('ErrorBoundary reset');
      }}
    >
      {children}
    </ReactErrorBoundary>
  );
}

export default ErrorBoundary;
